import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { apiService } from '@/services/apiService';
import { ArrowLeft, ExternalLink, Plus, Trash2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const COLUMNS = [
  { id: 'planejado', label: 'Planejado', color: '#6b7280' },
  { id: 'aprovado', label: 'Aprovado', color: '#3b82f6' },
  { id: 'concluido', label: 'Concluído', color: '#10b981' },
];

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Task modal state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    desc: '',
    status: 'planejado',
    priority: 3,
    risk_level: 'medium',
    owner_agent: 'linus',
    project_id: null,
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      const [projectData, tasksData] = await Promise.all([
        apiService.getProject(id),
        apiService.getTasks({ project_id: id }),
      ]);
      setProject(projectData);
      // Usar status direto do backend (backlog, todo, doing, done)
      setTasks(tasksData);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = () => {
    setSelectedTask(null);
    setFormData({
      title: '',
      desc: '',
      status: 'planejado',
      priority: 3,
      risk_level: 'medium',
      owner_agent: 'linus',
      project_id: parseInt(id),
    });
    setDialogOpen(true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setFormData({
      title: task.title,
      desc: task.desc || '',
      status: task.status,
      priority: task.priority,
      risk_level: task.risk_level,
      owner_agent: task.owner_agent,
      project_id: task.project_id,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedTask) {
        await apiService.updateTask(selectedTask.id, formData);
        toast.success('Tarefa atualizada');
      } else {
        await apiService.createTask(formData);
        toast.success('Tarefa criada');
      }
      setDialogOpen(false);
      setSelectedTask(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar tarefa');
    }
  };

  const handleDeleteTask = async () => {
    if (!selectedTask) return;
    
    if (!window.confirm(`Excluir tarefa "${selectedTask.title}"?`)) return;
    
    try {
      await apiService.deleteTask(selectedTask.id);
      toast.success('Tarefa excluída');
      setDialogOpen(false);
      setSelectedTask(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir tarefa');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Carregando...</div>;
  }

  if (!project) {
    return <div className="flex items-center justify-center h-96">Projeto não encontrado</div>;
  }

  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'concluido').length;
  const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div data-testid="project-detail-page">
      <Button
        variant="ghost"
        className="mb-4 uppercase text-xs"
        onClick={() => navigate('/projetos')}
        data-testid="back-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar
      </Button>

      <PageHeader
        title={project.name}
        description={project.goal}
      />

      <div className="mb-6 rounded-lg border border-border/50 bg-card/40 backdrop-blur-sm p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <span className="text-xs uppercase text-muted-foreground">Status</span>
            <div className="mt-2">
              <StatusBadge status={project.status} />
            </div>
          </div>
          <div>
            <span className="text-xs uppercase text-muted-foreground">Criado em</span>
            <p className="mt-2 text-sm font-mono">{formatDate(project.created_at)}</p>
          </div>
          <div>
            <span className="text-xs uppercase text-muted-foreground">Progresso</span>
            <div className="mt-2">
              <div className="flex items-center gap-2">
                <div className="h-2 flex-1 rounded-full bg-background/60 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-xs font-bold font-mono">{progress}%</span>
              </div>
            </div>
          </div>
        </div>

        {project.notes && (
          <div className="mt-4 pt-4 border-t border-border/50">
            <span className="text-xs uppercase text-muted-foreground">Notas</span>
            <p className="mt-2 text-sm">{project.notes}</p>
          </div>
        )}

        {project.links && project.links.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border/50">
            <span className="text-xs uppercase text-muted-foreground">Links</span>
            <div className="mt-2 space-y-2">
              {project.links.map((link, i) => (
                <a
                  key={i}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  {link.title}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Kanban do Projeto */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold uppercase tracking-wider">Kanban do Projeto</h2>
          <Button
            onClick={handleCreateTask}
            className="bg-primary hover:bg-primary/90 rounded-sm uppercase text-xs h-9 px-3"
            data-testid="create-task-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Tarefa
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {COLUMNS.map((column) => {
            const columnTasks = tasks.filter((t) => t.status === column.id);
            return (
              <div
                key={column.id}
                className="rounded-lg border border-border/50 bg-card/30 backdrop-blur-md overflow-hidden"
                data-testid={`project-kanban-column-${column.id}`}
              >
                <div
                  className="p-3 flex items-center justify-between border-b border-border/50"
                  style={{ borderTopColor: column.color, borderTopWidth: '2px' }}
                >
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider">
                    {column.label}
                  </h3>
                  <span className="text-xs font-mono text-muted-foreground" style={{ color: column.color }}>
                    {columnTasks.length}
                  </span>
                </div>
                <div className="p-3 space-y-2 min-h-[120px]">
                  {columnTasks.map((task) => (
                    <div
                      key={task.id}
                      className="rounded-md border border-border/50 bg-background/60 p-3 cursor-pointer hover:border-primary/50 transition-all"
                      onClick={() => handleEditTask(task)}
                      data-testid="project-task-card"
                    >
                      <h4 className="text-sm font-medium line-clamp-2 mb-2">{task.title}</h4>
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusBadge status={task.risk_level} />
                        <span className="text-[10px] font-mono text-muted-foreground uppercase">
                          P{task.priority}
                        </span>
                        <span className="text-[10px] font-mono text-muted-foreground uppercase">
                          {task.owner_agent}
                        </span>
                      </div>
                    </div>
                  ))}
                  {columnTasks.length === 0 && (
                    <div className="flex items-center justify-center h-20 text-xs text-muted-foreground/50">
                      Nenhuma tarefa
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Task Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121215] border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedTask ? 'Editar Tarefa' : 'Nova Tarefa'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title" className="text-xs uppercase tracking-wider">Título</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="mt-1.5"
                data-testid="task-title-input"
              />
            </div>
            <div>
              <Label htmlFor="desc" className="text-xs uppercase tracking-wider">Descrição</Label>
              <Textarea
                id="desc"
                value={formData.desc}
                onChange={(e) => setFormData({ ...formData, desc: e.target.value })}
                className="mt-1.5"
                rows={3}
                data-testid="task-desc-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status" className="text-xs uppercase tracking-wider">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger className="mt-1.5" data-testid="task-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planejado">Planejado</SelectItem>
                    <SelectItem value="aprovado">Aprovado</SelectItem>
                    
                    <SelectItem value="concluido">Concluído</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="priority" className="text-xs uppercase tracking-wider">Prioridade</Label>
                <Select value={String(formData.priority)} onValueChange={(value) => setFormData({ ...formData, priority: parseInt(value) })}>
                  <SelectTrigger className="mt-1.5" data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">P1 - Crítica</SelectItem>
                    <SelectItem value="2">P2 - Alta</SelectItem>
                    <SelectItem value="3">P3 - Média</SelectItem>
                    <SelectItem value="4">P4 - Baixa</SelectItem>
                    <SelectItem value="5">P5 - Mínima</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="risk_level" className="text-xs uppercase tracking-wider">Nível de Risco</Label>
                <Select value={formData.risk_level} onValueChange={(value) => setFormData({ ...formData, risk_level: value })}>
                  <SelectTrigger className="mt-1.5" data-testid="task-risk-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baixo</SelectItem>
                    <SelectItem value="medium">Médio</SelectItem>
                    <SelectItem value="high">Alto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="owner_agent" className="text-xs uppercase tracking-wider">Agente</Label>
                <Select value={formData.owner_agent} onValueChange={(value) => setFormData({ ...formData, owner_agent: value })}>
                  <SelectTrigger className="mt-1.5" data-testid="task-agent-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="main">Tom</SelectItem>
                    <SelectItem value="linus">Linus</SelectItem>
                    <SelectItem value="atlas">Atlas</SelectItem>
                    <SelectItem value="sys">SYS</SelectItem>
                    <SelectItem value="fred">Fred</SelectItem>
                    <SelectItem value="mark">Mark</SelectItem>
                    <SelectItem value="iris">Íris</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 justify-between pt-4 border-t border-border/30">
              {/* Delete button - only show when editing */}
              {selectedTask ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleDeleteTask}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 uppercase text-xs"
                  data-testid="delete-task-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  Excluir
                </Button>
              ) : (
                <div /> // Spacer
              )}
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    setSelectedTask(null);
                  }}
                  className="uppercase text-xs"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary/90 uppercase text-xs"
                  data-testid="submit-task-btn"
                >
                  {selectedTask ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
